import { Container, Point, Rectangle, Sprite } from "pixi.js"
import { TileSize } from "game-common/models"

interface TileGroup {
    container: Container
    cells: Map<string, Rectangle[]>
    left: number
    top: number
    right: number
    bottom: number
}

/** Rebuilt with map artwork; each frame queries only cells touched by the player's pose. */
export class RenderedTileOcclusion {
    private groups = new Map<Container, TileGroup>()
    private topLeft = new Point()
    private bottomRight = new Point()
    private localTopLeft = new Point()
    private localBottomRight = new Point()

    clear(): void { this.groups.clear() }

    add(container: Container, bounds: Rectangle): void {
        let group = this.groups.get(container)
        if (!group) {
            group = { container, cells: new Map(), left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
            this.groups.set(container, group)
        }
        group.left = Math.min(group.left, bounds.x)
        group.top = Math.min(group.top, bounds.y)
        group.right = Math.max(group.right, bounds.right)
        group.bottom = Math.max(group.bottom, bounds.bottom)
        for (let x = Math.floor(bounds.x / TileSize); x < Math.ceil(bounds.right / TileSize); x++) {
            for (let y = Math.floor(bounds.y / TileSize); y < Math.ceil(bounds.bottom / TileSize); y++) {
                const key = `${x},${y}`
                let tiles = group.cells.get(key)
                if (!tiles) group.cells.set(key, tiles = [])
                tiles.push(bounds)
            }
        }
    }

    overlaps(bounds: Rectangle): boolean {
        return this.find(bounds, () => true)
    }

    /** Pushes each drawn tile over `bounds` onto `out` as a screen-space [left, top, right, bottom] run. */
    collect(bounds: Rectangle, out: number[]): void {
        this.find(bounds, (tile, container) => {
            const matrix = container.worldTransform
            const left = matrix.a * tile.x + matrix.c * tile.y + matrix.tx
            const top = matrix.b * tile.x + matrix.d * tile.y + matrix.ty
            const right = matrix.a * tile.right + matrix.c * tile.bottom + matrix.tx
            const bottom = matrix.b * tile.right + matrix.d * tile.bottom + matrix.ty
            out.push(Math.min(left, right), Math.min(top, bottom), Math.max(left, right), Math.max(top, bottom))
            return false
        })
    }

    /** Visits the drawn tiles over `bounds` (in their container's space) until `visit` returns true. */
    private find(bounds: Rectangle, visit: (tile: Rectangle, container: Container) => boolean): boolean {
        this.topLeft.set(bounds.x, bounds.y)
        this.bottomRight.set(bounds.right, bounds.bottom)
        for (const group of this.groups.values()) {
            const { container, cells } = group
            if (!container.worldVisible) continue
            container.toLocal(this.topLeft, undefined, this.localTopLeft)
            container.toLocal(this.bottomRight, undefined, this.localBottomRight)
            if (container.worldAlpha <= 0.15) continue
            const left = Math.min(this.localTopLeft.x, this.localBottomRight.x)
            const top = Math.min(this.localTopLeft.y, this.localBottomRight.y)
            const right = Math.max(this.localTopLeft.x, this.localBottomRight.x)
            const bottom = Math.max(this.localTopLeft.y, this.localBottomRight.y)
            if (left >= group.right || right <= group.left || top >= group.bottom || bottom <= group.top) continue
            for (let x = Math.floor(left / TileSize); x < Math.ceil(right / TileSize); x++) {
                for (let y = Math.floor(top / TileSize); y < Math.ceil(bottom / TileSize); y++) {
                    const tiles = cells.get(`${x},${y}`)
                    if (!tiles) continue
                    for (const tile of tiles) {
                        if (tile.x < right && tile.right > left && tile.y < bottom && tile.bottom > top &&
                            visit(tile, container)) return true
                    }
                }
            }
        }
        return false
    }
}

/**
 * Screen bounds carried into each sprite's parent's own space, so a sprite clear of them is passed over on
 * its position alone. Measuring a sprite (getBounds) updates its transform and its parents' first: a storey
 * holds thousands of wall and tile sprites, and asking that of every one cost milliseconds a frame.
 */
export class SpriteBoundsFilter {
    private bounds = new Rectangle()
    private parents = new Map<Container, Rectangle>()
    private spare: Rectangle[] = []
    private lastParent?: Container
    private lastBox?: Rectangle
    private corner = new Point()
    private local = new Point()

    reset(bounds: Rectangle): this {
        this.bounds.copyFrom(bounds)
        this.parents.forEach(box => this.spare.push(box))
        this.parents.clear()
        this.lastParent = this.lastBox = undefined
        return this
    }

    /** False only when the sprite's quad lies wholly outside the bounds; anything it cannot tell, true. */
    mayOverlap(sprite: Sprite): boolean {
        const parent = sprite.parent, texture = sprite.texture
        if (!parent || !texture || sprite.rotation || sprite.skew.x || sprite.skew.y || sprite.children.length) return true
        const box = parent === this.lastParent ? this.lastBox : this.boxIn(parent)
        const { width, height } = texture.orig
        const x0 = sprite.position.x + sprite.scale.x * (-sprite.anchor.x * width - sprite.pivot.x)
        const x1 = x0 + sprite.scale.x * width
        const y0 = sprite.position.y + sprite.scale.y * (-sprite.anchor.y * height - sprite.pivot.y)
        const y1 = y0 + sprite.scale.y * height
        // Written as a miss, so a degenerate parent (NaN) never rejects anything.
        return !(Math.max(x0, x1) <= box.x || Math.min(x0, x1) >= box.right ||
            Math.max(y0, y1) <= box.y || Math.min(y0, y1) >= box.bottom)
    }

    private boxIn(parent: Container): Rectangle {
        let box = this.parents.get(parent)
        if (!box) {
            box = this.spare.pop() ?? new Rectangle()
            const { x, y, right, bottom } = this.bounds
            let left = Infinity, top = Infinity, far = -Infinity, low = -Infinity
            for (let i = 0; i < 4; i++) {
                this.corner.set(i & 1 ? right : x, i & 2 ? bottom : y)
                // The first corner brings the parent's transform up to date; the rest reuse it.
                parent.toLocal(this.corner, undefined, this.local, i > 0)
                left = Math.min(left, this.local.x); far = Math.max(far, this.local.x)
                top = Math.min(top, this.local.y); low = Math.max(low, this.local.y)
            }
            box.x = left; box.y = top; box.width = far - left; box.height = low - top
            this.parents.set(parent, box)
        }
        this.lastParent = parent
        this.lastBox = box
        return box
    }
}

const xs: number[] = []
const ys: number[] = []

/**
 * Whether the [left, top, right, bottom] runs in `rects` leave no part of `bounds` showing. A gap thinner
 * than `slack` px either way is a seam between two pieces of art meeting at fractional pixels, not a view.
 */
export const rectsCover = (bounds: Rectangle, rects: readonly number[], slack = 1): boolean => {
    if (bounds.width <= 0 || bounds.height <= 0) return false
    // Every run edge inside the bounds splits them into cells each wholly in or out of every run.
    xs.length = ys.length = 0
    xs.push(bounds.x, bounds.right)
    ys.push(bounds.y, bounds.bottom)
    for (let i = 0; i < rects.length; i += 4) {
        if (rects[i] > bounds.x && rects[i] < bounds.right) xs.push(rects[i])
        if (rects[i + 2] > bounds.x && rects[i + 2] < bounds.right) xs.push(rects[i + 2])
        if (rects[i + 1] > bounds.y && rects[i + 1] < bounds.bottom) ys.push(rects[i + 1])
        if (rects[i + 3] > bounds.y && rects[i + 3] < bounds.bottom) ys.push(rects[i + 3])
    }
    xs.sort((a, b) => a - b)
    ys.sort((a, b) => a - b)
    for (let i = 0; i + 1 < xs.length; i++) {
        const width = xs[i + 1] - xs[i]
        if (width <= 0) continue
        const x = xs[i] + width / 2
        for (let j = 0; j + 1 < ys.length; j++) {
            const height = ys[j + 1] - ys[j]
            if (height <= 0) continue
            const y = ys[j] + height / 2
            let covered = false
            for (let k = 0; k < rects.length && !covered; k += 4) {
                covered = rects[k] <= x && rects[k + 2] >= x && rects[k + 1] <= y && rects[k + 3] >= y
            }
            if (!covered && width >= slack && height >= slack) return false
        }
    }
    return true
}
